import { useEffect } from 'react';

import { useMaplibreMap } from '@App/components/MaplibreMap/maplibre-map.context';
import { useMapPopup, useLayerHover } from '@App/components/MaplibreMap/maplibre-map.hooks';
import { removeLayerAndSource } from '@App/components/MaplibreMap/maplibre-map.utils';
import { useObservations } from '@services/query.service';
import TranslationsProvider from '@utils/i18n/i18n';
import translator from '@App/app.translator';

import ObservationsPopup from './ObservationsPopup/ObservationsPopup';

const SOURCE_ID = 'catalan-observations';
const FILL_LAYER_ID = 'catalan-observations-fill';
const LABEL_LAYER_ID = 'catalan-observations-labels';

function ObservationsLayer() {
  const { map, isLoading } = useMaplibreMap();
  const { data: observations } = useObservations();
  const { openPopup, closePopup } = useMapPopup();

  useLayerHover(map, FILL_LAYER_ID);

  useEffect(() => {
    if (!map || isLoading) return undefined;

    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });

    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': '#5FB22F',
        'fill-opacity': 0.35,
        'fill-outline-color': '#4a8e26',
      },
    }, 'z-index-2');

    map.addLayer({
      id: LABEL_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      layout: {
        'text-field': ['get', 'number', ['get', 'observationCounts']],
        'text-size': 12,
        'text-anchor': 'center',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#4a8e26',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 2,
      },
    }, 'z-index-5');

    return () => {
      closePopup();
      removeLayerAndSource(map, LABEL_LAYER_ID);
      removeLayerAndSource(map, FILL_LAYER_ID, SOURCE_ID);
    };
  }, [isLoading, map, openPopup, closePopup]);

  useEffect(() => {
    if (!map) return undefined;

    const handleClick = (e) => {
      if (!e.features?.[0]) return;

      const feature = observations?.find(f => String(f.properties.id) === String(e.features[0].properties.id));

      if (!feature) return;

      openPopup(
        map,
        e.lngLat,
        <TranslationsProvider translator={translator}>
          <ObservationsPopup feature={feature} />
        </TranslationsProvider>,
      );
    };

    map.on('click', FILL_LAYER_ID, handleClick);

    return () => {
      map?.off('click', FILL_LAYER_ID, handleClick);
    };
  }, [map, observations, openPopup]);

  useEffect(() => {
    if (isLoading || !observations || !map?.getSource(SOURCE_ID)) return;

    map.getSource(SOURCE_ID).setData({
      type: 'FeatureCollection',
      features: observations,
    });
  }, [isLoading, observations, map]);

  return null;
}

export default ObservationsLayer;
